import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Modal from '@mui/material/Modal'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { handleOpenInNewTab } from '../../services/articleCardHandlers'
import { useAuth } from '../../context/AuthContext'
import {
  Box,
  Button,
  Typography,
  Paper,
  useMediaQuery,
  useTheme,
  Fab,
  IconButton,
  Zoom,
  Tooltip,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material'
import { parseHtmlSanitizeAddTargetToLinks } from '../../services/utils'
import TaxTreatyArticle from './TaxTreatyArticle'
import UpgradePromptModal from '../monetization/UpgradePromptModal'
import useFindScopeLayer from '../../hooks/useFindScopeLayer'

const TaxTreatyCard = ({ treaty, searchQuery, showSnackbar }) => {
  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isNarrow = useMediaQuery('(max-width:1029px)')
  const navigate = useNavigate()
  const { isAuthenticated, isLoading, quotaStatus } = useAuth()

  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState('success')
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const modalContentRef = useRef(null)

  // Scope browser Ctrl+F to this modal's content while it is open.
  useFindScopeLayer(modalIsOpen, 'card-modal')

  // Theme colors
  const themeColors = {
    primary: '#232536',
    secondary: '#ffcf51',
    hover: '#3a3e5a',
    background: '#f8f8f8',
  }
  const buttonBorderRadius = '28px'
  const handleModalToggle = useCallback(() => {
    // Check if user is authenticated before opening modal
    if (!isAuthenticated) {
      handleOpenInNewTab(
        treaty,
        `/tax-treaties/${treaty?.slug}`,
        showSnackbar,
      )
      return
    }

    // Only check quota when OPENING the modal
    if (!modalIsOpen) {
      // Check if DTAA quota is exhausted
      if (quotaStatus && !quotaStatus.hasActiveSubscription) {
        const remainingDtaaViews = quotaStatus.remainingDtaaViews ?? 0
        // const creditsBalance = quotaStatus.creditsBalance ?? 0

        // If quota exhausted, show upgrade modal instead of opening DTAA modal
        if (remainingDtaaViews === 0 /* || creditsBalance === 0 */) {
          setShowUpgradeModal(true)
          return
        }
      }
    }

    setModalIsOpen((prev) => !prev)
    // handleOpenInNewTab(treaty, `/tax-treaties/${treaty?.slug}`, showSnackbar)
  }, [treaty, isAuthenticated, showSnackbar, quotaStatus, modalIsOpen])
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return
    setSnackbarOpen(false)
  }

  const handleShowSnackbar = useCallback((message, severity = 'info') => {
    setSnackbarMessage(message || '')
    setSnackbarSeverity(severity || 'info')
    setSnackbarOpen(true)
  }, [])

  // Handle ESC key press - close modal only if no popups are open
  // Popup closing order: NestedPopup (top) -> MainPopup -> Modal (bottom)
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && modalIsOpen) {
        // Check if NestedPopup is open (highest priority - closes first)
        const nestedPopup = document.getElementById('nested-popup-container')
        if (nestedPopup) {
          // NestedPopup is open, let it handle its own ESC key
          return
        }
        
        // Check if MainPopup is open (second priority)
        const mainPopup = document.getElementById('main-popup-container')
        if (mainPopup) {
          // MainPopup is open, let it handle its own ESC key
          return
        }
        
        // No popups are open, close the Modal
        handleModalToggle()
      }
    }

    if (modalIsOpen) {
      document.addEventListener('keydown', handleEscKey)
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [modalIsOpen, handleModalToggle])

  // Set up scroll detection with direct DOM approach
  useEffect(() => {
    // Only run this if modal is open
    if (!modalIsOpen) return

    // Use requestAnimationFrame for smoother detection
    let scrollFrame
    const checkScroll = () => {
      if (modalContentRef.current) {
        // Check if scrolled down enough
        setShowBackToTop(modalContentRef.current.scrollTop > 300)
      }
      // Continue checking
      scrollFrame = requestAnimationFrame(checkScroll)
    }

    // Start the animation frame loop
    scrollFrame = requestAnimationFrame(checkScroll)

    // Add a basic scroll listener as backup
    const handleScroll = () => {
      if (modalContentRef.current) {
        setShowBackToTop(modalContentRef.current.scrollTop > 300)
      }
    }

    // Get the modal element
    const modalElement = modalContentRef.current
    if (modalElement) {
      modalElement.addEventListener('scroll', handleScroll)
    }

    // Clean up
    return () => {
      cancelAnimationFrame(scrollFrame)
      if (modalElement) {
        modalElement.removeEventListener('scroll', handleScroll)
      }
    }
  }, [modalIsOpen])

  // Function to scroll modal to top
  const scrollToTop = useCallback(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }, [])
  // Truncate long text
  // TODO1: updated version of truncateText is present only in ArticleCard and not in DecisionCard, GuideCard, TaxTreatyCard
  // issue: any html code should be ignored while displaying text of title/name/country2Name,  for e.g. "Article 26 - <a href='/decisions/uae-cit-fdl-47-2022-md-132-of-2023' target='_blank' rel='noopener noreferrer'>Transfers Within a Qualifying Group</a>" so this should be considered as ""Article 26 - Transfers Within a Qualifying Group" before using slicing,  but we dont want to remove the effect html tags were giving like bold italic text color change etc
  // TODO2: also tooltip should not show html code while hovering over title
  // const truncateText = (text, maxLength) => {
  //   if (!text) return ''
  //   if (text.length <= maxLength) return text
  //   return text.slice(0, maxLength) + '...'
  // }
  // const truncatedTitle = truncateText(article?.title, isMobileScreen ? 90 : 120)

  // const showTitleTooltip =
  //   article?.title && article.title.length > (isMobileScreen ? 90 : 120)

  return (
    <>
      <Paper
        onClick={handleModalToggle}
        sx={{
          margin: 2,
          padding: 2,
          cursor: 'pointer',
          // height: cardHeight

          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transform: 'translateY(-2px)',
          },
        }}
        id={`article-${treaty?.slug}`}
      >
        {/* Outer: flag always on left, right column handles rows */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {treaty?.flagCode && (
            <Tooltip title={treaty?.country2Name} arrow>
              <img
                loading='lazy'
                src={`https://flagcdn.com/40x30/${treaty.flagCode.toLowerCase()}.png`}
                srcSet={`https://flagcdn.com/80x60/${treaty?.flagCode.toLowerCase()}.png 2x,
    https://flagcdn.com/120x90/${treaty?.flagCode.toLowerCase()}.png 3x`}
                width='40'
                height='30'
                alt={`${treaty.country2Name}`}
                style={{ marginRight: '8px', flexShrink: 0 }}
              />
            </Tooltip>
          )}

          {/* Right column: grows to fill remaining space */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              minWidth: 0,
              gap: (isNarrow && treaty?.officialTranslation === false) ? 0.75 : 0,
            }}
          >
            {/* Row 1: Country Name + Open icon */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                minWidth: 0,
              }}
            >
              <Typography
                variant={isMobileScreen ? 'subtitle1' : 'h6'}
                sx={{
                  cursor: 'pointer',
                  color: themeColors.primary,
                  fontWeight: 600,
                  lineHeight: 1.4,
                  flexGrow: 1,
                  minWidth: 0,
                  fontSize: isMobileScreen ? '1rem' : undefined,
                  '& a': {
                    pointerEvents: 'none',
                    color: 'inherit',
                    textDecoration: 'inherit',
                  },
                }}
              >
                {parseHtmlSanitizeAddTargetToLinks(
                  treaty?.country2Name,
                  searchQuery
                )}
              </Typography>

              {/* Inline chips — always shown unless narrow AND unofficial chip is present */}
              {!(isNarrow && treaty?.officialTranslation === false) && (
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: 1 }}>
                  {treaty?.officialTranslation === false && (
                    <Box sx={{ ml: 1 }}>
                      <Chip
                        label='Unofficial / Best effort translation'
                        size='small'
                        sx={{
                          bgcolor: '#808080',
                          color: '#ffffff',
                          fontWeight: 600,
                          height: 24,
                          fontSize: '0.75rem',
                          px: 1.2,
                        }}
                      />
                    </Box>
                  )}
                  {!treaty?.title && (
                    <Box sx={{ ml: 1 }}>
                      <Chip
                        label='Not Available'
                        size='small'
                        sx={{
                          bgcolor: '#d32f2f',
                          color: '#ffffff',
                          fontWeight: 600,
                          height: 24,
                          fontSize: '0.75rem',
                          px: 1.2,
                        }}
                      />
                    </Box>
                  )}
                  {treaty?.betaVersion && (
                    <Box sx={{ ml: 1 }}>
                      <Chip
                        label='Beta Version'
                        size='small'
                        sx={{
                          bgcolor: '#e3f2fd',
                          color: '#1976d2',
                          fontWeight: 600,
                          height: 24,
                          fontSize: '0.75rem',
                          px: 1.2,
                          border: '1px solid rgba(25, 118, 210, 0.25)',
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}

              {/* Open in new tab*/}
              <Box sx={{ flexShrink: 0, ml: 1 }}>
                <Tooltip title='Open in new tab' arrow>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation()
                      handleOpenInNewTab(treaty, `/tax-treaties/${treaty?.slug}`)
                    }}
                    size={isMobileScreen ? 'small' : 'medium'}
                    sx={{
                      color: themeColors.primary,
                      '&:hover': {
                        backgroundColor: 'rgba(35, 37, 54, 0.08)',
                      },
                    }}
                  >
                    <OpenInNewRoundedIcon
                      fontSize={isMobileScreen ? 'small' : 'medium'}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Row 2: Beta Version chip — only when stacked mode (narrow + unofficial) */}
            {isNarrow && treaty?.officialTranslation === false && treaty?.betaVersion && (
              <Chip
                label='Beta Version'
                size='small'
                sx={{
                  alignSelf: 'flex-start',
                  bgcolor: '#e3f2fd',
                  color: '#1976d2',
                  fontWeight: 600,
                  height: 24,
                  fontSize: '0.75rem',
                  px: 1.2,
                  border: '1px solid rgba(25, 118, 210, 0.25)',
                }}
              />
            )}

            {/* Row 3: Unofficial chip — narrow only */}
            {isNarrow && treaty?.officialTranslation === false && (
              <Chip
                label='Unofficial / Best effort translation'
                size='small'
                sx={{
                  alignSelf: 'flex-start',
                  bgcolor: '#808080',
                  color: '#ffffff',
                  fontWeight: 600,
                  height: 24,
                  fontSize: '0.75rem',
                  px: 1.2,
                }}
              />
            )}

            {/* Not Available — narrow + unofficial only */}
            {isNarrow && treaty?.officialTranslation === false && !treaty?.title && (
              <Chip
                label='Not Available'
                size='small'
                sx={{
                  alignSelf: 'flex-start',
                  bgcolor: '#d32f2f',
                  color: '#ffffff',
                  fontWeight: 600,
                  height: 24,
                  fontSize: '0.75rem',
                  px: 1.2,
                }}
              />
            )}
          </Box>
        </Box>
        <Modal open={modalIsOpen} onClose={handleModalToggle} disableEscapeKeyDown disableEnforceFocus>
          {/* Wrapper Box to maintain border radius when scrollbar appears */}
          <Box
            data-find-scope='card-modal'
            onClick={(e) => e.stopPropagation()}
            sx={{
              margin: '5vh auto',
              width: isMobileScreen ? '95vw' : isMediumScreen ? '90vw' : '85vw',
              maxHeight: '90vh',
              borderRadius: '8px',
              overflow: 'hidden', // Hide any content that would break the border radius
              position: 'relative',
              display: 'flex', // Use flexbox
              flexDirection: 'column', // Stack content vertically
              backgroundColor: 'white',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
          >
            {/* Content Box with scrolling */}
            <Box
              ref={modalContentRef}
              sx={{
                padding: '1px 6px 16px 6px', // Add padding inside the content area
                paddingTop: 2,
                position: 'relative',
                overflowY: 'auto', // Enable vertical scrolling
                flexGrow: 1, // Allow this box to expand
                // Ensure scrollbar doesn't affect layout
                scrollbarWidth: 'thin', // For Firefox
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '0 8px 8px 0', // Round the scrollbar track
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
              }}
            >
              {/* RESPONSIVE CLOSE BUTTON */}
              {isMobileScreen ? (
                // Smaller close button for mobile
                <IconButton
                  onClick={handleModalToggle}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    float: 'right',
                    zIndex: 1500,
                    backgroundColor: 'white',
                    border: '1px solid #232536',
                    margin: '-8px -8px 0 0',
                    right: '-2px', // Use right positioning for exact alignment
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  }}
                  aria-label='close modal'
                  size='small'
                >
                  <CloseIcon sx={{ color: '#232536' }} fontSize='small' />
                </IconButton>
              ) : (
                // Larger close button with text for desktop/tablet
                <Box
                  sx={{
                    position: 'sticky',
                    cursor: 'pointer',
                    top: 0,
                    float: 'right',
                    zIndex: 1500,
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    border: '1px solid #232536',
                    borderRadius: buttonBorderRadius,
                    margin: '-4px -4px 0 0',
                    right: 0,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    padding: '4px 12px',
                  }}
                  onClick={handleModalToggle}
                  role='button'
                  aria-label='close modal'
                >
                  <CloseIcon
                    sx={{ color: themeColors.primary }}
                    fontSize='small'
                  />
                  <Box sx={{ alignSelf: 'center' }}>
                    <Typography
                      variant='button'
                      sx={{
                        color: themeColors.primary,
                        marginLeft: 0.5,
                        fontSize: '0.80rem',
                        fontWeight: 'bold',
                      }}
                    >
                      Close
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Conditionally render TaxTreatyArticle component only if authenticated */}
              {isLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    padding: 4,
                  }}
                >
                  <CircularProgress sx={{ color: themeColors.primary }} />
                  <Typography
                    variant='body2'
                    sx={{
                      mt: 2,
                      color: 'text.secondary',
                    }}
                  >
                    Checking authentication...
                  </Typography>
                </Box>
              ) : isAuthenticated ? (
                <TaxTreatyArticle
                  treaty={treaty}
                  searchQuery={searchQuery}
                  showSnackbar={handleShowSnackbar}
                  isInModal={true}
                  parentContainerRef={modalContentRef}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    padding: 4,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant='h5'
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: themeColors.primary,
                    }}
                  >
                    Authentication Required
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{
                      mb: 3,
                      color: 'text.secondary',
                      maxWidth: '500px',
                    }}
                  >
                    Please log in to view this tax treaty. You need to be authenticated
                    to access tax treaty content.
                  </Typography>
                  <Button
                    variant='contained'
                    onClick={() => {
                      // Store current path for redirect after login
                      sessionStorage.setItem(
                        'redirectPath',
                        `/tax-treaties/${treaty?.slug}`
                      )
                      navigate('/login')
                    }}
                    sx={{
                      backgroundColor: themeColors.primary,
                      color: 'white',
                      padding: '10px 24px',
                      borderRadius: buttonBorderRadius,
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: themeColors.hover,
                      },
                    }}
                  >
                    Go to Login
                  </Button>
                </Box>
              )}
              <Box
                sx={{
                  position: 'sticky',
                  bottom: isMobileScreen ? 20 : 25,
                  // right: -2,
                  float: 'right',
                  marginTop: '-60px',
                  zIndex: 1400,
                }}
              >
                <Zoom in={showBackToTop}>
                  {isMobileScreen ? (
                    // Small circular Fab for mobile
                    <Fab
                      size='small'
                      aria-label='Scroll back to top'
                      onClick={scrollToTop}
                      sx={{
                        backgroundColor: themeColors.primary,
                        color: 'white',
                        '&:hover': { backgroundColor: themeColors.hover },
                      }}
                    >
                      <KeyboardArrowUpIcon fontSize='small' />
                    </Fab>
                  ) : (
                    // Extended Fab with text for desktop/tablet
                    <Fab
                      variant='extended'
                      size='medium'
                      aria-label='Scroll back to top'
                      onClick={scrollToTop}
                      sx={{
                        backgroundColor: themeColors.primary,
                        color: 'white',
                        '&:hover': { backgroundColor: themeColors.hover },
                        paddingLeft: 2,
                        paddingRight: 2,
                        borderRadius: buttonBorderRadius,
                      }}
                    >
                      <KeyboardArrowUpIcon sx={{ mr: 1 }} />
                      <Typography
                        variant='button'
                        sx={{
                          // color: themeColors.primary,
                          // marginLeft: 0.5,
                          // fontSize: '0.80rem',
                          fontWeight: 'bold',
                        }}
                      >
                        Back to Top
                      </Typography>
                    </Fab>
                  )}
                </Zoom>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Paper>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={1500}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <UpgradePromptModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        reason='quota_exhausted'
        quotaResetDate={quotaStatus?.quotaResetDate}
        documentType='dtaa'
        forceDialog
      />
    </>
  )
}

export default TaxTreatyCard
